import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarCheck, CalendarX, Link2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Requested", "Confirmed", "Rescheduled", "Completed", "Cancelled", "No Show"];

function CalendarCard() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["calendar-status"], queryFn: () => api.get("/calendar/status").then(r => r.data) });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("calendar");
    if (p === "connected") { toast.success("Google Calendar connected"); qc.invalidateQueries({ queryKey: ["calendar-status"] }); }
    else if (p === "error") toast.error("Google Calendar connection failed. Please try again.");
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, [qc]);

  const connect = async () => {
    try { const { data } = await api.get("/calendar/connect"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Unable to start Google authorization"); }
  };
  const disconnect = async () => {
    try { await api.post("/calendar/disconnect"); qc.invalidateQueries({ queryKey: ["calendar-status"] }); toast.success("Google Calendar disconnected"); }
    catch { toast.error("Failed to disconnect"); }
  };

  if (!status) return null;

  if (!status.configured) {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4 text-sm text-amber-800" data-testid="calendar-notconfigured">
        <div className="flex items-center gap-2 font-medium"><CalendarX className="h-4 w-4" /> Google Calendar sync isn't configured yet</div>
        <p className="mt-1 text-amber-700">Add <code className="font-mono bg-amber-100 px-1 rounded">GOOGLE_CLIENT_ID</code> and <code className="font-mono bg-amber-100 px-1 rounded">GOOGLE_CLIENT_SECRET</code> from Google Cloud Console to enable two-way appointment sync. Appointments are managed manually until then.</p>
      </div>
    );
  }

  if (status.connected) {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 mb-4 flex items-center justify-between gap-3" data-testid="calendar-connected">
        <div className="flex items-center gap-2 text-sm text-emerald-800">
          <CalendarCheck className="h-4 w-4" />
          <span>Synced to Google Calendar <span className="font-medium">{status.email}</span>. Confirmed appointments are added automatically.</span>
        </div>
        {status.canManage && (
          <button onClick={disconnect} data-testid="calendar-disconnect-btn" className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-100 transition-colors">Disconnect</button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 mb-4 flex items-center justify-between gap-3" data-testid="calendar-disconnected">
      <div className="flex items-center gap-2 text-sm text-blue-900">
        <Link2 className="h-4 w-4" />
        <span>Connect Google Calendar to automatically add confirmed appointments as calendar events.</span>
      </div>
      {status.canManage ? (
        <button onClick={connect} data-testid="calendar-connect-btn" className="shrink-0 inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md bg-[#0052FF] text-white hover:bg-[#0043cc] transition-colors">
          <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_18dp.png" alt="" className="h-3.5 w-3.5 bg-white rounded-sm p-px" /> Connect Google Calendar
        </button>
      ) : <span className="text-xs text-blue-500">Ask an owner or admin to connect.</span>}
    </div>
  );
}

const SYNC_BADGE = {
  synced: { label: "Synced", cls: "bg-emerald-100 text-emerald-700" },
  error: { label: "Sync failed", cls: "bg-red-100 text-red-700" },
  not_connected: { label: "—", cls: "bg-slate-100 text-slate-400" },
};

export default function Appointments() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["owner", "admin"].includes(user?.role);
  const { data: appts } = useQuery({ queryKey: ["appointments"], queryFn: () => api.get("/appointments").then(r => r.data.appointments) });
  const setStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/appointments/${id}/status`, null, { params: { status } });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      if (data.calendarSync === "synced") toast.success("Updated & synced to Google Calendar");
      else if (data.calendarSync === "error") toast.warning("Status updated, but calendar sync failed");
      else toast.success("Updated");
    } catch (e) { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    await api.delete(`/appointments/${id}`);
    qc.invalidateQueries({ queryKey: ["appointments"] });
    toast.success("Appointment deleted");
  };
  return (
    <div>
      <PageTitle title="Appointments" subtitle="Requests remain pending until you confirm them" />
      <CalendarCard />
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="appointments-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Type</th><th className="text-left p-3">Requested</th><th className="text-left p-3">Location</th><th className="text-left p-3">Status</th><th className="text-left p-3">Calendar</th>{canDelete && <th className="text-left p-3"></th>}</tr>
          </thead>
          <tbody>
            {(appts || []).length === 0 ? <tr><td colSpan={canDelete ? 7 : 6} className="p-8 text-center text-slate-400">No appointments yet.</td></tr> :
              (appts || []).map((a) => {
                const badge = SYNC_BADGE[a.calendarSync] || SYNC_BADGE.not_connected;
                return (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`appt-row-${a.id}`}>
                  <td className="p-3 font-medium text-navy">{a.name}<span className="block text-xs text-slate-400">{a.email}</span></td>
                  <td className="p-3 text-slate-600">{a.serviceType}</td>
                  <td className="p-3 text-slate-600">{a.requestedDate ? new Date(a.requestedDate).toLocaleString() : "—"}</td>
                  <td className="p-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{a.locationType}</span></td>
                  <td className="p-3"><select value={a.status} onChange={e => setStatus(a.id, e.target.value)} className="text-xs rounded-md border border-slate-200 px-2 py-1" data-testid={`appt-status-${a.id}`}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></td>
                  <td className="p-3">
                    {a.calendarLink ? (
                      <a href={a.calendarLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:underline" data-testid={`appt-calendar-link-${a.id}`}>Synced <ExternalLink className="h-3 w-3" /></a>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                    )}
                  </td>
                  {canDelete && <td className="p-3"><ConfirmDelete onConfirm={() => remove(a.id)} title="Delete this appointment?" description={`${a.serviceType} — ${a.name} will be removed${a.calendarEventId ? " (and its Google Calendar event)" : ""}.`} testid={`appt-delete-${a.id}`} /></td>}
                </tr>
              );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}
